import React from 'react'
import { bindActionCreators } from 'redux'
import { connect } from 'react-redux'
import { Link } from 'react-router-dom'
import LoadingProgress from '../components/LoadingProgress'
import ErrorMessage from '../components/ErrorMessage'
import { fetchMachines } from '../actions/machines'
import * as XLSX from 'xlsx'

import '../styles/SoftwareVersionsView.css'

const OS_LABELS = {
  1: 'Raspbian',
  2: 'Ubuntu'
}

function getOSLabel(os) {
  return OS_LABELS[os] || `Desconocido (${os})`
}

class SoftwareVersionsView extends React.Component {
  constructor(props) {
    super(props)
    this.state = {
      filterOS: 'all',
      filterVersion: '',
      sortBy: 'version',
      expandedGroups: {},
      filtersOpen: true
    }
  }

  componentDidMount() {
    this.props.fetchMachines()
  }

  toggleGroup = (key) => {
    this.setState(prev => ({
      expandedGroups: {
        ...prev.expandedGroups,
        [key]: !prev.expandedGroups[key]
      }
    }))
  }

  expandAll = (groups) => {
    const expanded = {}
    groups.forEach(g => { expanded[g.key] = true })
    this.setState({ expandedGroups: expanded })
  }

  collapseAll = () => {
    this.setState({ expandedGroups: {} })
  }

  handleDownloadExcel = () => {
    const { machines } = this.props
    if (!machines) return

    const data = machines
      .filter(m => m.Sw_version)
      .map(m => ({
        Serial: m.Serial || '-',
        Cliente: m.Customer ? m.Customer.Fullname : '-',
        Estado: m.Status || '-',
        'Sistema Operativo': getOSLabel(m.OS || 1),
        'Versión SW': m.Sw_version || '-',
        'Versión FW': m.Fw_version || '-',
        'Versión ESP32': m.ESP32_version || '-',
        'Versión Updater': m.Up_version || '-',
      }))

    const worksheet = XLSX.utils.json_to_sheet(data)
    const workbook = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Versiones SW')
    XLSX.writeFile(workbook, 'versiones_software.xlsx')
  }

  render() {
    const { machines, loading, error } = this.props
    const { filterOS, filterVersion, expandedGroups } = this.state

    if (error) {
      return <ErrorMessage message={error.message} />
    }

    if (!machines || loading) {
      return <LoadingProgress />
    }

    // Filter machines
    let filtered = machines.filter(m => m.Sw_version)

    if (filterOS !== 'all') {
      filtered = filtered.filter(m => String(m.OS) === filterOS)
    }

    if (filterVersion) {
      filtered = filtered.filter(m =>
        m.Sw_version.toLowerCase().includes(filterVersion.toLowerCase())
      )
    }

    // Group by Sw_version + OS
    const groupMap = {}
    filtered.forEach(machine => {
      const key = `${machine.Sw_version}__${machine.OS || 1}`
      if (!groupMap[key]) {
        groupMap[key] = {
          key,
          version: machine.Sw_version,
          os: machine.OS || 1,
          machines: []
        }
      }
      groupMap[key].machines.push(machine)
    })

    const groups = Object.values(groupMap).sort((a, b) => {
      if (a.version < b.version) return 1
      if (a.version > b.version) return -1
      return a.os - b.os
    })

    // Collect unique OS values for filter
    const osValues = [...new Set(machines.filter(m => m.Sw_version).map(m => m.OS || 1))].sort()

    // Summary stats
    const totalMachines = filtered.length
    const totalVersions = groups.length

    return (
      <div className="sw-admin-view">
        {/* Header */}
        <div className="sw-admin-header">
          <div className="sw-header-content">
            <div className="sw-header-text">
              <h1>
                <i className="fas fa-code mr-3"></i>
                <span className="text-vw-dark mr-1">Versiones</span>
                <span className="text-vw-light">Software</span>
              </h1>
            </div>
            <div className="sw-header-actions">
              <button
                className="sw-admin-btn success"
                onClick={this.handleDownloadExcel}
              >
                <i className="fas fa-file-excel"></i>
                <span>Exportar Excel</span>
              </button>
              <button
                className="sw-admin-btn secondary"
                onClick={() => this.expandAll(groups)}
              >
                <i className="fas fa-expand-alt"></i>
                <span>Expandir todo</span>
              </button>
              <button
                className="sw-admin-btn outline"
                onClick={this.collapseAll}
              >
                <i className="fas fa-compress-alt"></i>
                <span>Colapsar todo</span>
              </button>
            </div>
          </div>
        </div>

        {/* KPI Cards */}
        <div className="sw-kpi-row">
          <div className="sw-kpi-card kpi-total">
            <div className="sw-kpi-icon">
              <i className="fas fa-desktop"></i>
            </div>
            <div className="sw-kpi-data">
              <div className="sw-kpi-label">Máquinas</div>
              <div className="sw-kpi-value">{totalMachines}</div>
            </div>
          </div>
          <div className="sw-kpi-card kpi-versions">
            <div className="sw-kpi-icon">
              <i className="fas fa-code-branch"></i>
            </div>
            <div className="sw-kpi-data">
              <div className="sw-kpi-label">Versiones</div>
              <div className="sw-kpi-value">{totalVersions}</div>
            </div>
          </div>
          <div className="sw-kpi-card kpi-os">
            <div className="sw-kpi-icon">
              <i className="fas fa-server"></i>
            </div>
            <div className="sw-kpi-data">
              <div className="sw-kpi-label">Sistemas OS</div>
              <div className="sw-kpi-value">{osValues.length}</div>
            </div>
          </div>
        </div>

        {/* Filter Card */}
        <div className="sw-filter-card">
          <div className="sw-filter-header" onClick={() => this.setState(prev => ({ filtersOpen: !prev.filtersOpen }))}>
            <h3>
              <i className="fas fa-filter"></i>
              Filtros
            </h3>
            <i className={`fas fa-chevron-down sw-toggle-icon ${this.state.filtersOpen !== false ? 'open' : ''}`}></i>
          </div>
          {this.state.filtersOpen !== false && (
            <div className="sw-filter-body">
              <div className="sw-filter-grid">
                <div className="sw-filter-group">
                  <label>Sistema Operativo</label>
                  <select
                    value={filterOS}
                    onChange={e => this.setState({ filterOS: e.target.value })}
                    className="sw-filter-control"
                  >
                    <option value="all">Todos</option>
                    {osValues.map(os => (
                      <option key={os} value={String(os)}>{getOSLabel(os)}</option>
                    ))}
                  </select>
                </div>
                <div className="sw-filter-group">
                  <label>Buscar versión</label>
                  <input
                    type="text"
                    value={filterVersion}
                    onChange={e => this.setState({ filterVersion: e.target.value })}
                    placeholder="Filtrar por versión..."
                    className="sw-filter-control"
                  />
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Grouped list */}
        {groups.length === 0 ? (
          <div className="sw-empty-state">
            <i className="fas fa-search"></i>
            <p>No se encontraron máquinas con los filtros seleccionados.</p>
          </div>
        ) : (
          <div className="sw-versions-groups">
            {groups.map(group => (
              <div key={group.key} className={`sw-version-group ${expandedGroups[group.key] ? 'is-open' : ''}`}>
                <div
                  className="sw-version-header"
                  onClick={() => this.toggleGroup(group.key)}
                >
                  <div className="sw-version-info">
                    <div className={`sw-version-accent os-${group.os}`}></div>
                    <span className="sw-version-name">{group.version}</span>
                    <span className={`sw-os-badge os-${group.os}`}>
                      {getOSLabel(group.os)}
                    </span>
                  </div>
                  <div className="sw-version-meta">
                    <span className="sw-machine-count">
                      <i className="fas fa-desktop"></i>
                      {group.machines.length}
                    </span>
                    <i className={`fas fa-chevron-down sw-expand-icon ${expandedGroups[group.key] ? 'expanded' : ''}`}></i>
                  </div>
                </div>
                {expandedGroups[group.key] && (
                  <div className="sw-version-machines">
                    <table className="sw-machines-table">
                      <thead>
                        <tr>
                          <th>Serial</th>
                          <th>Cliente</th>
                          <th>Estado</th>
                          <th>FW</th>
                          <th>ESP32</th>
                          <th>Updater</th>
                        </tr>
                      </thead>
                      <tbody>
                        {group.machines.map(machine => (
                          <tr key={machine._id}>
                            <td>
                              <Link to={`/machines/${machine._id}`} className="sw-serial-link">
                                {machine.Serial}
                              </Link>
                            </td>
                            <td>{machine.Customer ? machine.Customer.Fullname : '-'}</td>
                            <td>
                              <span className={`sw-status-badge status-${(machine.Status || '').toLowerCase()}`}>
                                {machine.Status || '-'}
                              </span>
                            </td>
                            <td><span className="sw-version-cell">{machine.Fw_version || '-'}</span></td>
                            <td><span className="sw-version-cell">{machine.ESP32_version || '-'}</span></td>
                            <td><span className="sw-version-cell">{machine.Up_version || '-'}</span></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    )
  }
}

const mapStateToProps = state => ({
  machines: state.machines.machines,
  error: state.machines.error,
  loading: state.machines.loading,
  role: state.auth.role,
  type: state.auth.type
})

const mapDispatchToProps = dispatch => ({
  fetchMachines: bindActionCreators(fetchMachines, dispatch)
})

export default connect(mapStateToProps, mapDispatchToProps)(SoftwareVersionsView)
